// Fase 17.3, Plano 11 (D-10) — "Tintas e rendimento": o PCP informa quantos m² cada kg de tinta cobre; a liberação da OP usa esse valor
// (com o fator de perda da cabine) para prever o consumo de tinta. Materiais em kg sem tipo ou cor aparecem à parte para corrigir em Cadastros.
import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { ListaTintasDto, RendimentoPadraoTipoDto, TintaRendimentoDto, UnidadeTinta } from '@pieltec/contracts';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Checkbox } from '../../../components/ui/checkbox';
import { Input } from '../../../components/ui/input';
import { Skeleton } from '../../../components/ui/skeleton';
import { opcoesTinta } from '../../../lib/material-atributos';
import { obterSessaoAtual } from '../../../lib/session';
import { atualizarRendimentoDaTinta, definirRendimentoPadraoDoTipo, definirTintaPadrao, listarTintas } from '../../../lib/roteiros/tintas-api';
import {
  contarUsadasSemRendimento,
  camposPendentesTinta,
  filtrarCoresComVariasTintas,
  filtrarUsadasSemRendimento,
  marcarPadraoNoGrupo,
  mensagemPadraoDefinido,
  podeUsarComoPadrao,
  rendimentoMudou,
  rendimentoParaApi,
  rendimentoParaCampo,
  rotuloUsarComoPadrao,
  rotuloUnidadeRendimento,
  TEXTO_CORRECAO_CADASTRO,
  TEXTO_SO_USADAS_SEM_RENDIMENTO,
  TEXTO_SO_VARIAS_TINTAS,
  unidadeSugeridaDoTipo,
  usadaSemRendimento,
  validarPadraoDoTipo,
  validarRendimento,
} from '../../../lib/roteiros/tintas';

const TEXTO_REFERENCIA_TPI = 'Referência TPI para tinta em pó: cerca de 8,5 m²/kg na aplicação manual e 13 m²/kg na automática. Use o valor da ficha técnica da tinta.';
const CABECALHO = 'px-2 py-2 text-xs font-semibold uppercase tracking-wider';
const rotuloDoTipo = (tipo: string | null): string => (tipo === null ? 'Não informado' : (opcoesTinta.find((opcao) => opcao.value === tipo)?.label ?? tipo));

interface LinhaProps {
  readonly tinta: TintaRendimentoDto;
  readonly podeEditar: boolean;
  readonly onSalvo: (materialId: string, rendimento: string | null) => void;
  readonly onUsarComoPadrao: (tinta: TintaRendimentoDto) => void;
  readonly definindoPadrao: boolean;
}

function LinhaTinta({ tinta, podeEditar, onSalvo, onUsarComoPadrao, definindoPadrao }: LinhaProps) {
  const [texto, setTexto] = useState(rendimentoParaCampo(tinta.rendimentoM2PorKg));
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);

  useEffect(() => { setTexto(rendimentoParaCampo(tinta.rendimentoM2PorKg)); }, [tinta.rendimentoM2PorKg]);

  const erroDeValidacao = validarRendimento(texto);
  const mudou = rendimentoMudou(tinta.rendimentoM2PorKg, texto);
  const idCampo = `tinta-rendimento-${tinta.materialId}`;

  const salvar = async (): Promise<void> => {
    if (erroDeValidacao !== null) { setErro(erroDeValidacao); return; }
    setSalvando(true);
    setErro(null);
    setSalvo(false);
    try {
      const gravado = await atualizarRendimentoDaTinta(tinta.materialId, rendimentoParaApi(texto));
      setSalvo(true);
      onSalvo(tinta.materialId, gravado);
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível salvar o rendimento. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <tr className="border-t align-top">
      <td className="px-2 py-2 font-semibold">{tinta.codigo}</td>
      <td className="px-2 py-2">{tinta.nome}</td>
      <td className="px-2 py-2">{rotuloDoTipo(tinta.tipoTinta)}</td>
      <td className="px-2 py-2">{tinta.unidade}</td>
      <td className="px-2 py-2">{tinta.corCodigo ?? 'Sem cor'}</td>
      <td className="px-2 py-2">
        {tinta.padrao ? (
          <Badge variant="secondary" className="font-semibold">Padrão</Badge>
        ) : podeUsarComoPadrao(tinta, podeEditar) ? (
          <Button type="button" variant="outline" size="sm" disabled={definindoPadrao} onClick={() => onUsarComoPadrao(tinta)}>{rotuloUsarComoPadrao(tinta)}</Button>
        ) : null}
      </td>
      <td className="px-2 py-2 tabular-nums">{tinta.pecasLigadas}</td>
      <td className="px-2 py-2 tabular-nums">{tinta.pecasQueUsam}</td>
      <td className="px-2 py-2">
        {podeEditar ? (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <label htmlFor={idCampo} className="sr-only">{`Rendimento em ${rotuloUnidadeRendimento(tinta.unidade)} de ${tinta.codigo}`}</label>
              <Input
                id={idCampo}
                inputMode="decimal"
                className="w-32"
                placeholder="Ex.: 12,5"
                value={texto}
                aria-invalid={erro !== null}
                onChange={(evento) => { setTexto(evento.target.value); setErro(null); setSalvo(false); }}
              />
              <Button type="button" size="sm" disabled={salvando || !mudou} onClick={() => void salvar()}>{salvando ? 'Salvando...' : 'Salvar'}</Button>
            </div>
            {usadaSemRendimento(tinta) ? <Badge variant="outline">Usada sem rendimento</Badge> : null}
            {erro ? <p role="alert" className="text-xs text-destructive">{erro}</p> : null}
            {salvo ? <p role="status" className="text-xs text-muted-foreground">Rendimento salvo.</p> : null}
          </div>
        ) : (
          <span className="tabular-nums">{tinta.rendimentoM2PorKg === null ? 'Não informado' : `${rendimentoParaCampo(tinta.rendimentoM2PorKg)} ${rotuloUnidadeRendimento(tinta.unidade)}`}</span>
        )}
      </td>
    </tr>
  );
}

function LinhaPadraoTipo({ padrao, podeEditar, onSalvo }: { readonly padrao: RendimentoPadraoTipoDto; readonly podeEditar: boolean; readonly onSalvo: (valor: RendimentoPadraoTipoDto) => void }) {
  const [texto, setTexto] = useState(rendimentoParaCampo(padrao.rendimentoM2PorUnidade));
  const [unidade, setUnidade] = useState<UnidadeTinta>(padrao.unidade ?? unidadeSugeridaDoTipo(padrao.tipoTinta));
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState(false);
  useEffect(() => { setTexto(rendimentoParaCampo(padrao.rendimentoM2PorUnidade)); setUnidade(padrao.unidade ?? unidadeSugeridaDoTipo(padrao.tipoTinta)); }, [padrao]);
  const validar = validarPadraoDoTipo(texto, unidade);
  const salvar = async (): Promise<void> => {
    if (validar !== null) { setErro(validar); return; }
    setSalvando(true); setErro(null); setSucesso(false);
    try { onSalvo(await definirRendimentoPadraoDoTipo(padrao.tipoTinta, rendimentoParaApi(texto), texto.trim() === '' ? null : unidade)); setSucesso(true); }
    catch (falha) { setErro(falha instanceof Error ? falha.message : 'Não foi possível salvar o rendimento padrão. Tente novamente.'); }
    finally { setSalvando(false); }
  };
  if (!podeEditar) return <div className="flex flex-col gap-2"><span className="text-sm font-semibold">{rotuloDoTipo(padrao.tipoTinta)}</span><span className="text-sm">{padrao.rendimentoM2PorUnidade === null || padrao.unidade === null ? 'Não definido' : `${rendimentoParaCampo(padrao.rendimentoM2PorUnidade)} ${rotuloUnidadeRendimento(padrao.unidade)}`}</span></div>;
  return <div className="flex flex-wrap items-center gap-2"><span className="w-full text-sm font-semibold">{rotuloDoTipo(padrao.tipoTinta)}</span><Input aria-label={`Rendimento padrão ${rotuloDoTipo(padrao.tipoTinta)}`} inputMode="decimal" className="w-32" placeholder="Ex.: 8,5" value={texto} onChange={(evento) => { setTexto(evento.target.value); setErro(null); setSucesso(false); }} /><div className="flex gap-1" role="group" aria-label={`Unidade ${rotuloDoTipo(padrao.tipoTinta)}`}><Button type="button" size="sm" variant={unidade === 'kg' ? 'secondary' : 'outline'} onClick={() => setUnidade('kg')}>kg</Button><Button type="button" size="sm" variant={unidade === 'l' ? 'secondary' : 'outline'} onClick={() => setUnidade('l')}>L</Button></div><Button type="button" size="sm" disabled={salvando} onClick={() => void salvar()}>{salvando ? 'Salvando...' : 'Salvar'}</Button>{erro ? <p role="alert" className="text-xs text-destructive">{erro}</p> : null}{sucesso ? <p role="status" className="text-xs text-muted-foreground">Rendimento padrão salvo.</p> : null}</div>;
}

export function TintasRoteiroPage() {
  const [dados, setDados] = useState<ListaTintasDto | null>(null);
  const [erro, setErro] = useState(false);
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [somenteSemRendimento, setSomenteSemRendimento] = useState(false);
  const [somenteVariasTintas, setSomenteVariasTintas] = useState(false);
  const [somenteUsadasSemRendimento, setSomenteUsadasSemRendimento] = useState(false);
  const [definindoPadrao, setDefinindoPadrao] = useState(false);
  const [mensagemPadrao, setMensagemPadrao] = useState<string | null>(null);
  const [erroPadrao, setErroPadrao] = useState<string | null>(null);
  const [permissoes, setPermissoes] = useState<readonly string[]>([]);

  // A autorização real é do servidor; aqui o "Salvar" só aparece para quem pode (D-16).
  useEffect(() => {
    let vivo = true;
    void obterSessaoAtual().then((sessao) => {
      if (vivo && sessao.autenticado) setPermissoes(sessao.usuario.permissions);
    });
    return () => { vivo = false; };
  }, []);

  useEffect(() => {
    const temporizador = setTimeout(() => setBuscaAplicada(busca), 300);
    return () => clearTimeout(temporizador);
  }, [busca]);

  const carregar = useCallback(async (sinal?: { vivo: boolean }) => {
    setErro(false);
    try {
      const lista = await listarTintas({ busca: buscaAplicada, somenteSemRendimento });
      if (!sinal || sinal.vivo) setDados(lista);
    } catch {
      if (!sinal || sinal.vivo) setErro(true);
    }
  }, [buscaAplicada, somenteSemRendimento]);

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => { sinal.vivo = false; };
  }, [carregar]);

  const podeEditar = permissoes.includes('roteiros:gerenciar');
  // Com "Só sem rendimento" ligado a tinta salva sai da lista na hora; do contrário só o valor da linha muda.
  const aoSalvar = (materialId: string, rendimento: string | null): void => {
    setDados((atual) => {
      if (atual === null) return atual;
      const tintas = atual.tintas
        .map((tinta) => (tinta.materialId === materialId ? { ...tinta, rendimentoM2PorKg: rendimento } : tinta))
        .filter((tinta) => (!somenteSemRendimento || tinta.rendimentoM2PorKg === null) && (!somenteUsadasSemRendimento || !usadaSemRendimento(tinta)));
      return { ...atual, tintas };
    });
  };

  // Quick 261017-gwz: a troca é feita no servidor; aqui só se espelha o resultado (a anterior do mesmo grupo deixa de ser padrão).
  const usarComoPadrao = async (tinta: TintaRendimentoDto): Promise<void> => {
    setDefinindoPadrao(true);
    setMensagemPadrao(null);
    setErroPadrao(null);
    try {
      const resultado = await definirTintaPadrao(tinta.materialId, true);
      setDados((atual) => (atual === null ? atual : { ...atual, tintas: marcarPadraoNoGrupo(atual.tintas, tinta.materialId) }));
      setMensagemPadrao(mensagemPadraoDefinido(tinta, resultado.padraoAnterior));
    } catch (falha) {
      setErroPadrao(falha instanceof Error ? falha.message : 'Não foi possível definir a tinta padrão. Tente novamente.');
    } finally {
      setDefinindoPadrao(false);
    }
  };

  const incompletas = dados?.incompletas ?? [];
  const usadasSemRendimento = contarUsadasSemRendimento(dados?.tintas ?? []);
  const tintasVisiveis = filtrarUsadasSemRendimento(filtrarCoresComVariasTintas(dados?.tintas ?? [], somenteVariasTintas), somenteUsadasSemRendimento);
  const aoSalvarPadrao = (salvo: RendimentoPadraoTipoDto): void => setDados((atual) => atual === null ? atual : { ...atual, padroesPorTipo: atual.padroesPorTipo.map((padrao) => padrao.tipoTinta === salvo.tipoTinta ? salvo : padrao) });

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-6">
      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex flex-col gap-1">
              <h1 className="text-[28px] font-semibold leading-tight">Tintas e rendimento</h1>
              <p className="text-sm text-muted-foreground">Consulte as tintas das peças e os rendimentos usados no cálculo do consumo.</p>
            </div>
            <div className="flex flex-wrap gap-2"><Button asChild type="button" variant="outline"><a href="/pcp/roteiros">Roteiros das peças</a></Button><Button asChild type="button" variant="outline" className="h-auto min-h-10 whitespace-normal text-left"><a href="/pcp/roteiros?semTintaDaPeca=true">Peças pintadas sem tinta da peça</a></Button></div>
          </div>
          <div className="grid gap-4 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <div className="flex flex-col gap-1">
              <label htmlFor="tintas-busca" className="text-sm font-semibold">Buscar tinta</label>
              <Input id="tintas-busca" type="search" placeholder="Código ou nome da tinta" value={busca} onChange={(evento) => setBusca(evento.target.value)} />
            </div>
            <div className="flex flex-col justify-end gap-2">
              <label htmlFor="tintas-sem-rendimento" className="flex items-center gap-2 text-sm">
                <Checkbox id="tintas-sem-rendimento" checked={somenteSemRendimento} onCheckedChange={(marcado) => setSomenteSemRendimento(marcado === true)} />
                Só sem rendimento
              </label>
              <label htmlFor="tintas-varias-tintas" className="flex items-center gap-2 text-sm">
                <Checkbox id="tintas-varias-tintas" checked={somenteVariasTintas} onCheckedChange={(marcado) => setSomenteVariasTintas(marcado === true)} />
                {TEXTO_SO_VARIAS_TINTAS}
              </label>
              <label htmlFor="tintas-usadas-sem-rendimento" className="flex items-center gap-2 text-sm">
                <Checkbox id="tintas-usadas-sem-rendimento" checked={somenteUsadasSemRendimento} onCheckedChange={(marcado) => setSomenteUsadasSemRendimento(marcado === true)} />
                {TEXTO_SO_USADAS_SEM_RENDIMENTO}
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {dados ? (
        <Card>
          <CardContent className="flex flex-col gap-4">
            <h2 className="text-sm font-semibold">Rendimento padrão por tipo</h2>
            <p className="text-xs text-muted-foreground">Vale para as tintas sem rendimento próprio, na mesma unidade: a OP calcula e marca "rendimento estimado". Deixe em branco para não usar padrão.</p>
            <div className={podeEditar ? 'grid gap-4 lg:grid-cols-3' : 'grid gap-4 md:grid-cols-3'}>
              {dados.padroesPorTipo.map((padrao) => (
                <div key={padrao.tipoTinta} className="min-w-0 rounded-md border p-4">
                  <LinhaPadraoTipo padrao={padrao} podeEditar={podeEditar} onSalvo={aoSalvarPadrao} />
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">{TEXTO_REFERENCIA_TPI}</p>
          </CardContent>
        </Card>
      ) : null}

      {usadasSemRendimento > 0 ? <section role="group" aria-label="Tintas usadas sem rendimento" className="min-w-0 rounded border border-warning bg-card p-4"><h2 className="flex gap-2 text-sm font-semibold text-foreground"><AlertTriangle aria-hidden="true" className="size-4 shrink-0 text-warning" />{`Tintas usadas sem rendimento: ${usadasSemRendimento}`}</h2><p className="mt-2 text-xs text-muted-foreground">Estas tintas estão ligadas a peças e ainda calculam pelo rendimento padrão do tipo (ou ficam a definir). Cadastre o rendimento delas primeiro.</p></section> : null}

      {incompletas.length > 0 ? (
        <section role="group" aria-label="Aviso de cadastro de tintas" className="min-w-0 rounded border border-warning bg-card p-4">
          <h2 className="flex gap-2 text-sm font-semibold text-foreground">
            <AlertTriangle aria-hidden="true" className="size-4 shrink-0 text-warning" />
            {`Tintas com cadastro incompleto: ${incompletas.length}`}
          </h2>
          <p className="mt-2 text-xs text-muted-foreground">{TEXTO_CORRECAO_CADASTRO}</p>
          <ul className="mt-2 space-y-2 text-sm text-foreground">
            {incompletas.map((material) => (
              <li key={material.materialId} className="min-w-0 break-words">
                <span className="font-semibold">{material.codigo}</span>
                {` ${material.nome} · tipo: ${rotuloDoTipo(material.tipoTinta)} · cor: ${material.corCodigo ?? 'Não informada'}`}
                <p className="font-semibold">Falta informar: {camposPendentesTinta(material).join(' e ')}.</p>
                <a className="inline-flex min-h-11 items-center underline" href={`/cadastros/materiais?busca=${encodeURIComponent(material.codigo)}`}>Corrigir cadastro de {material.codigo}</a>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {mensagemPadrao ? <p role="status" className="text-sm text-muted-foreground">{mensagemPadrao}</p> : null}
      {erroPadrao ? <p role="alert" className="text-sm text-destructive">{erroPadrao}</p> : null}

      {erro ? (
        <Card>
          <CardContent role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
            <p className="text-sm text-destructive">Não foi possível carregar as tintas. Verifique a conexão e tente novamente.</p>
            <Button type="button" variant="outline" onClick={() => void carregar()}>Tentar novamente</Button>
          </CardContent>
        </Card>
      ) : dados === null ? (
        <div className="flex flex-col gap-2" aria-busy="true" aria-label="Carregando tintas">
          {Array.from({ length: 6 }).map((_, indice) => <Skeleton key={indice} className="h-8 w-full" />)}
        </div>
      ) : tintasVisiveis.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <p className="text-sm font-semibold">Nenhuma tinta encontrada.</p>
            <p className="text-sm text-muted-foreground">Aparecem aqui as tintas ativas em kg ou litro com tipo de tinta e cor, e as ligadas a alguma peça mesmo sem cor (Cadastros → Materiais).</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="gap-0 overflow-hidden py-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <caption className="sr-only">Tintas e rendimento (m² por kg ou por litro)</caption>
              <thead className="bg-muted text-left">
                <tr>
                  {['Código', 'Tinta', 'Tipo', 'Unidade', 'Cor', 'Padrão', 'Peças ligadas', 'Peças da cor', 'Rendimento'].map((titulo) => (
                    <th key={titulo} scope="col" className={CABECALHO}>{titulo}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tintasVisiveis.map((tinta) => (
                  <LinhaTinta key={tinta.materialId} tinta={tinta} podeEditar={podeEditar} onSalvo={aoSalvar} onUsarComoPadrao={(alvo) => void usarComoPadrao(alvo)} definindoPadrao={definindoPadrao} />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
